// Session-based encryption utility for secure workflow data transmission
// Implements the hybrid approach with session-specific keys

import crypto from 'crypto-js';
import logger from './logger';

interface SessionKeyData {
    sessionId: string;
    sessionKey: string;
    expiresAt: number;
}

interface EncryptedData {
    encrypted: string;
    iv: string;
    sessionId: string;
}

class SessionEncryption {
    private currentSession: SessionKeyData | null = null;
    private readonly API_BASE_URL = '/api';

    /**
     * Get or generate a session key for encryption
     */
    async getSessionKey(): Promise<SessionKeyData> {
        // Check if current session is still valid
        if (this.currentSession && this.currentSession.expiresAt > Date.now()) {
            logger.log('🔑 Using existing session key');
            return this.currentSession;
        }

        // Generate new session key from backend
        try {
            logger.log('🔑 Requesting new session key from backend...');

            // CRITICAL FIX: Get Auth0 token for session key request
            const { getAuthTokenForAPI } = await import('../services/workflowStorageService');
            const authToken = await getAuthTokenForAPI();

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            // Add Authorization header if token is available
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
                logger.log('🔐 Including Auth0 token in session key request');
            } else {
                logger.warn('⚠️ No Auth0 token available - session key request may fail');
            }

            const response = await fetch(`${this.API_BASE_URL}/workflow-storage?action=generate-session-key`, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to generate session key');
            }

            this.currentSession = {
                sessionId: data.sessionId,
                sessionKey: data.sessionKey,
                expiresAt: data.expiresAt
            };

            // CRITICAL FIX: Store complete session data in sessionStorage for workflow key fetching
            sessionStorage.setItem('workflow_session_data', JSON.stringify(this.currentSession));

            logger.log('✅ New session key generated:', this.currentSession.sessionId);
            logger.log('✅ Session data stored in sessionStorage for workflow key access');
            return this.currentSession;

        } catch (error) {
            // Don't log as error - session encryption is optional, fallback to server-side encryption
            logger.log('ℹ️ Session key generation unavailable, will use server-side encryption fallback');
            throw new Error(`Session key generation failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Encrypt data using session-specific key
     */
    async encryptWithSession(data: any): Promise<EncryptedData> {
        try {
            const session = await this.getSessionKey();
            const dataString = typeof data === 'string' ? data : JSON.stringify(data);

            logger.log('🔐 Encrypting data with session key...');

            // Generate random IV for each encryption
            const iv = crypto.lib.WordArray.random(16);

            // Encrypt using AES-256-CBC with session key
            const encrypted = crypto.AES.encrypt(dataString, crypto.enc.Hex.parse(session.sessionKey), {
                iv: iv,
                mode: crypto.mode.CBC,
                padding: crypto.pad.Pkcs7
            });

            const result: EncryptedData = {
                encrypted: encrypted.toString(),
                iv: iv.toString(crypto.enc.Hex),
                sessionId: session.sessionId
            };

            logger.log('✅ Data encrypted with session key');
            return result;

        } catch (error) {
            // Don't log as error - this is expected fallback behavior
            logger.log('ℹ️ Session encryption unavailable, using server-side encryption fallback');
            throw new Error(`Session encryption failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Decrypt data using session-specific key
     */
    async decryptWithSession(encryptedData: EncryptedData): Promise<string> {
        try {
            // Verify we have the correct session
            if (!this.currentSession || this.currentSession.sessionId !== encryptedData.sessionId) {
                throw new Error('Session key mismatch - cannot decrypt data');
            }

            logger.log('🔓 Decrypting data with session key...');

            // Decrypt using AES-256-CBC
            const decrypted = crypto.AES.decrypt(encryptedData.encrypted, crypto.enc.Hex.parse(this.currentSession.sessionKey), {
                iv: crypto.enc.Hex.parse(encryptedData.iv),
                mode: crypto.mode.CBC,
                padding: crypto.pad.Pkcs7
            });

            const decryptedString = decrypted.toString(crypto.enc.Utf8);

            if (!decryptedString) {
                throw new Error('Decryption resulted in empty string');
            }

            logger.log('✅ Data decrypted with session key');
            return decryptedString;

        } catch (error) {
            logger.error('❌ Session decryption failed:', error);
            throw new Error(`Session decryption failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    }

    /**
     * Clear current session (for security)
     */
    clearSession(): void {
        this.currentSession = null;
        sessionStorage.removeItem('workflow_session_data');
        logger.log('🧹 Session cleared from memory and sessionStorage');
    }

    /**
     * Check if session is valid
     */
    isSessionValid(): boolean {
        return this.currentSession !== null && this.currentSession.expiresAt > Date.now();
    }

    /**
     * Get session info for debugging
     */
    getSessionInfo(): { sessionId: string | null; expiresAt: number | null; isValid: boolean } {
        return {
            sessionId: this.currentSession?.sessionId || null,
            expiresAt: this.currentSession?.expiresAt || null,
            isValid: this.isSessionValid()
        };
    }
}

// Export singleton instance
export const sessionEncryption = new SessionEncryption();
export default sessionEncryption;

// Helper functions for easy use
export async function encryptForSession(data: any): Promise<EncryptedData> {
    return sessionEncryption.encryptWithSession(data);
}

export async function decryptFromSession(encryptedData: EncryptedData): Promise<string> {
    return sessionEncryption.decryptWithSession(encryptedData);
}

export function clearCurrentSession(): void {
    sessionEncryption.clearSession();
}

export function getSessionStatus(): { sessionId: string | null; expiresAt: number | null; isValid: boolean } {
    return sessionEncryption.getSessionInfo();
}
